import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import supabase from '../supabase';
import { v4 as uuidv4 } from 'uuid';
import MessageRenderer from './MessageRenderer';

const Chat = ({ user, currentThread, setCurrentThread, threadName }) => {
  const [searchParams] = useSearchParams();
  const flowId = searchParams.get('flowId');
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [error, setError] = useState(null);
  const [enhancedSummary, setEnhancedSummary] = useState('');
  const [currentInteractiveMessageId, setCurrentInteractiveMessageId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Create a new thread for the given flowId.
  const createThreadForFlow = async (dialogFlowId) => {
    // First, fetch the dialog flow details to get the flow name.
    const { data: flowData, error: flowError } = await supabase
      .from('dialog_flows')
      .select('name')
      .eq('id', dialogFlowId)
      .single();
    
    if (flowError) {
      console.error('Error fetching dialog flow:', flowError);
      return null;
    }
    
    // Use the flow name if available.
    const threadName = flowData?.name ? `Dialog Flow: ${flowData.name}` : `Dialog Flow: ${dialogFlowId}`;
  
    // Now insert the new thread.
    const { data, error } = await supabase
      .from('threads')
      .insert({
        user_id: user.Id,
        name: threadName,
        flow_id: dialogFlowId,
        created_at: new Date().toISOString(),
      })
      .select('*')
      .single(); // or maybeSingle() if supported
  
    if (error) {
      console.error('Error creating thread:', error);
      return null;
    }
    return data;
  };
  


  // If a flowId exists but there's no currentThread, create one automatically.
  useEffect(() => {
    const initThreadForFlow = async () => {
      if (flowId && user && !currentThread) {
        const newThread = await createThreadForFlow(flowId);
        if (newThread) {
          setCurrentThread(newThread);
          // Optionally update the URL to remove flowId once the thread is created.
          navigate(`/assistant?threadId=${newThread.id}`, { replace: true });
        }
      }
    };
    initThreadForFlow();
  }, [flowId, user, currentThread, setCurrentThread, navigate]);

  // Fetch messages for the current thread.
  useEffect(() => {
    const fetchMessages = async (threadId) => {
      if (!threadId) {
        setMessages([]);
        setLoadingMessages(false);
        return;
      }
      setLoadingMessages(true);
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('thread_id', threadId)
        .order('created_at', { ascending: true });
      if (error) {
        console.error('Error fetching messages:', error.message);
        setError('Failed to load messages.');
        setLoadingMessages(false);
        return;
      }
      setMessages(data);
      setLoadingMessages(false);
    };

    if (user && currentThread?.id) {
      fetchMessages(currentThread.id);
    } else {
      setMessages([]);
      setLoadingMessages(false);
    }
  }, [user, currentThread]);

  // Scroll to bottom and focus input when messages update.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    inputRef.current?.focus();
  }, [messages]);

  // Load the first question of the dialog flow if flowId is present.
  useEffect(() => {
    const loadDialogFlow = async () => {
      if (!flowId || !user || !currentThread) return;
      const { data: questions, error } = await supabase
        .from('dialog_questions')
        .select('*')
        .eq('flow_id', flowId)
        .order('question_order', { ascending: true })
        .limit(1);
      if (error) {
        console.error('Error loading dialog flow question:', error.message);
        return;
      }
      if (questions && questions.length > 0) {
        const firstQuestion = questions[0];
        
        // Insert a pending record so that the API can continue the flow later.
        const { error: insertError } = await supabase
          .from('user_dialog_responses')
          .insert({
            user_id: user.id,
            flow_id: flowId,
            question_id: firstQuestion.id,
            response: null,
            thread_id: currentThread.id,
            question_order: firstQuestion.question_order,
            created_at: new Date().toISOString()
          });
        if (insertError) {
          console.error('Error inserting pending record:', insertError.message);
          return;
        }
        
        // Now display the first question as an assistant message.
        const flowMessage = {
          id: uuidv4(),
          role: 'assistant',
          content: `\n${firstQuestion.question_text}`,
          created_at: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, flowMessage]);
      }
    };

    loadDialogFlow();
  }, [flowId, user, currentThread]);

  const sendMessage = async (customMessage) => {
    const messageToSend = customMessage || input.trim();
    if (!messageToSend || !user || !currentThread?.id) return;

    const userMessage = {
      id: uuidv4(),
      role: 'user',
      content: messageToSend,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);
    if (!customMessage) setInput('');

    try {
      setSendingMessage(true);
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          message: messageToSend,
          threadId: currentThread.id,
          prevEnhancedSummary: enhancedSummary,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        const assistantMessage = {
          id: uuidv4(),
          role: 'assistant',
          content: data.reply,
          options: data.options || null,
          type: data.type || null,
          allowMultiple: data.allowMultiple || false,
          enhancedSummary: data.enhancedSummary || null,
          created_at: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, assistantMessage]);
        if (assistantMessage.type === "enhancedConfirmation") {
          setEnhancedSummary(assistantMessage.enhancedSummary);
          setCurrentInteractiveMessageId(assistantMessage.id);
        } else {
          setCurrentInteractiveMessageId(null);
          setEnhancedSummary('');
        }
      } else {
        setError(`Error: ${data.error || 'Something went wrong.'}`);
        const errorMsg = {
          id: uuidv4(),
          role: 'assistant',
          content: `Error: ${data.error || 'Something went wrong.'}`,
          created_at: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, errorMsg]);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again later.');
      const errorMsg = {
        id: uuidv4(),
        role: 'assistant',
        content: 'An unexpected error occurred. Please try again later.',
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleOptionClick = (value) => {
    let answer = value;
    if (Array.isArray(value)) {
      answer = value.join(', ');
    }
    if (answer === "edit") {
      setInput(enhancedSummary);
    } else if (answer === "approve") {
      sendMessage("approve");
    } else {
      sendMessage(answer);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleFormSubmit = async (formData) => {
    console.log("Form submitted:", formData);
    const formResponse = Object.entries(formData)
      .map(([key, value]) => `- **${key}**: ${value}`)
      .join('\n');
    const formMessage = {
      id: uuidv4(),
      role: 'user',
      content: formResponse,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, formMessage]);
  };

  return (
    <div className="flex flex-col h-full bg-gray-900">
      {/* Header */}
      <div className="p-4 border-b border-gray-800 bg-gray-900">
        <h2 className="text-lg font-semibold text-gray-200">
          {threadName ? `Thread: ${threadName}` : 'No Thread Selected'}
        </h2>
      </div>

      {/* Quick Prompts */}
      <div className="p-3 bg-gray-900 border-b border-gray-800">
        <p className="text-gray-300 mb-2">Quick Prompts:</p>
        <div className="flex space-x-2">
          <button
            onClick={() => setInput('/basics')}
            className="px-3 py-1 bg-black border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-900 transition"
          >
            Get the Basics
          </button>
          <button
            onClick={() => setInput('/advanced')}
            className="px-3 py-1 bg-black border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-900 transition"
          >
            Advanced Info
          </button>
          <button
            onClick={() => setInput('/resume')}
            className="px-3 py-1 bg-black border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-900 transition"
          >
            Resume Flow
          </button>
        </div>
      </div>

      {/* Chat messages */}
      <div className="flex-1 overflow-y-auto p-4">
        {loadingMessages && (
          <p className="text-center text-gray-400 mb-4">Loading messages...</p>
        )}
        {!loadingMessages && messages.length === 0 && (
          <p className="text-center text-gray-400 mb-4">
            No messages yet. Start the conversation!
          </p>
        )}
        <div className="flex flex-col space-y-4">
          {messages.map((msg) => {
            const isUser = msg.role === 'user';
            const showOptions =
              msg.options && msg.options.length > 0 && msg.id === currentInteractiveMessageId;
            return (
              <div key={msg.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                {!isUser && (
                  <div className="mr-2">
                    <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center text-gray-300 text-sm">
                      AI
                    </div>
                  </div>
                )}
                <div
                  className={`relative max-w-[80%] md:max-w-[60%] p-4 rounded-xl shadow-sm ${
                    isUser ? 'bg-blue-900 text-blue-100' : 'bg-gray-800 text-gray-100'
                  }`}
                >
                  <MessageRenderer
                    message={msg}
                    onFormSubmit={handleFormSubmit}
                    onOptionClick={handleOptionClick}
                  />
                </div>
                {isUser && (
                  <div className="ml-2">
                    <div className="w-8 h-8 bg-blue-700 rounded-full flex items-center justify-center text-blue-100 text-sm">
                      U
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {error && (
          <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded mt-4" role="alert">
            {error}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Chat input area */}
      <div className="sticky bottom-0 z-10 p-4 border-t border-gray-800 ">
        <div className="flex items-center space-x-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            rows="2"
            disabled={sendingMessage}
            className="flex-1 p-3 rounded-xl resize-none bg-black text-gray-200 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-600"
          />
          <button
            onClick={() => sendMessage()}
            disabled={sendingMessage}
            className={`px-4 py-2 rounded-xl text-white font-semibold ${
              sendingMessage ? 'bg-blue-700 cursor-not-allowed' : 'bg-blue-900 hover:bg-blue-800'
            } transition`}
          >
            {sendingMessage ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Chat;
